import { useState } from "react";
import {
  Form,
  Input,
  Select,
  Radio,
  Button,
  DatePicker,
  Upload,
  Space,
  Tooltip,
  Flex,
} from "antd";
import { InfoCircleOutlined, InboxOutlined } from "@ant-design/icons";
import { ICreateInvoice } from "@shared/lib/types/briefCampains";
import InputNumber from "@shared/ui/InputNumber";
import { FirebaseService, ToastService } from "@shared/lib/services";
import dayjs from "dayjs";

const { Option } = Select;
const { Dragger } = Upload;

export function InvoiceEntryForm({
  initialValues,
  onSubmit,
  isSubmitting,
  onCancel,
}: {
  initialValues?: Partial<ICreateInvoice>;
  onSubmit?: (values: ICreateInvoice) => void;
  isSubmitting?: boolean;
  onCancel?: () => void;
}) {
  const [form] = Form.useForm();
  const [fileUrl, setFileUrl] = useState<string | undefined>(
    initialValues?.url
  );

  const handleFileUpload = async (file: File) => {
    try {
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "invoices",
        file
      );
      setFileUrl(uploadedUrl);
      form.setFieldValue("url", uploadedUrl);
      ToastService.success("File uploaded successfully");
    } catch (error) {
      ToastService.error("File upload failed. Please try again.");
    }
  };
  const handleFinish = (values: any) => {
    const { invoiceDate, ...rest } = values;
    const finalValues = {
      ...rest,
      invoiceDate: invoiceDate ? invoiceDate.toISOString() : undefined,
      gstPercentage: values?.gstPercentage ?? 0,
      tdsPercentage: values?.tdsPercentage ?? 0,
      remarks: values?.remarks ? values.remarks.trim() : "",
      // ...other mapping logic
    };
    onSubmit?.(finalValues);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        ...initialValues,
        invoiceDate: initialValues?.invoiceDate
          ? dayjs(initialValues.invoiceDate)
          : undefined,
      }}
      onFinish={handleFinish}
    >
      <Form.Item
        label={
          <span>
            Choose Type
            <Tooltip
              title={
                <Flex vertical gap={4}>
                  <p>
                    <strong>Invoice:</strong> A document showing the amount
                    payable to the creator. Used to initiate or validate
                    payouts.
                  </p>
                  <p>
                    <strong>Credit Note:</strong> A reversal of a previously
                    issued invoice reduces payable amount. Used when campaigns
                    are cancelled or costs are adjusted.
                  </p>
                </Flex>
              }
            >
              <InfoCircleOutlined style={{ marginLeft: 6 }} />
            </Tooltip>
          </span>
        }
        name="type"
        rules={[{ required: true, message: "Please select type" }]}
      >
        <Select placeholder="Invoice or Credit Note" disabled={isSubmitting}>
          <Option value="Invoice">Invoice</Option>
          <Option value="Credit-Note">Credit Note</Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Invoice Number"
        name="remarks"
        rules={[{ required: true, message: "Please enter invoice number" }]}
      >
        <Input placeholder="Invoice no." disabled={isSubmitting} />
      </Form.Item>

      <Form.Item
        label="Invoice Date"
        name="invoiceDate"
        rules={[{ required: true, message: "Please select date" }]}
      >
        <DatePicker style={{ width: "100%" }} disabled={isSubmitting} />
      </Form.Item>

      <Form.Item
        label="Base Amount"
        name="baseCost"
        rules={[{ required: true, message: "Please enter amount" }]}
      >
        <InputNumber
          placeholder="Enter Amount"
          style={{ width: "100%" }}
          type="number"
          disabled={isSubmitting}
        />
      </Form.Item>

      <Form.Item
        label={
          <span>
            Does the ledger have <b>GST</b> ?
          </span>
        }
        name="hasGst"
        initialValue={initialValues?.hasGst ?? true}
      >
        <Radio.Group disabled={isSubmitting}>
          <Radio value={false}>No</Radio>
          <Radio value={true}>Yes, it Is</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        shouldUpdate={(prev, curr) =>
          prev.baseCost !== curr.baseCost ||
          prev.gstPercentage !== curr.gstPercentage ||
          prev.hasGst !== curr.hasGst
        }
      >
        {() => {
          const baseCost = form.getFieldValue("baseCost") || 0;
          const gstPercentage = form.getFieldValue("gstPercentage") ?? 18;
          const hasGst = form.getFieldValue("hasGst") ?? true;
          const gstValue = hasGst
            ? Math.round((baseCost * gstPercentage) / 100)
            : 0;
          return (
            hasGst && (
              <Space style={{ width: "100%" }}>
                <Form.Item
                  name="gstPercentage"
                  noStyle
                  initialValue={initialValues?.gstPercentage ?? 18}
                >
                  <InputNumber
                    suffix="%"
                    style={{ width: "100%" }}
                    type="number"
                    disabled={isSubmitting}
                    addonAfter={gstValue}
                  />
                </Form.Item>
              </Space>
            )
          );
        }}
      </Form.Item>

      <Form.Item
        label={
          <span>
            Does the ledger have <b>TDS</b> ?
          </span>
        }
        name="hasTds"
        initialValue={initialValues?.hasTds ?? true}
      >
        <Radio.Group disabled={isSubmitting}>
          <Radio value={false}>No</Radio>
          <Radio value={true}>Yes, it Is</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        shouldUpdate={(prev, curr) =>
          prev.baseCost !== curr.baseCost ||
          prev.tdsPercentage !== curr.tdsPercentage ||
          prev.hasTds !== curr.hasTds
        }
      >
        {() => {
          const baseCost = form.getFieldValue("baseCost") || 0;
          const tdsPercentage = form.getFieldValue("tdsPercentage") ?? 10;
          const hasTds = form.getFieldValue("hasTds") ?? true;
          const tdsValue = hasTds
            ? Math.round((baseCost * tdsPercentage) / 100)
            : 0;
          return (
            hasTds && (
              <Space style={{ width: "100%" }}>
                <Form.Item
                  name="tdsPercentage"
                  noStyle
                  initialValue={initialValues?.tdsPercentage ?? 2}
                >
                  <InputNumber
                    suffix="%"
                    style={{ width: "100%" }}
                    type="number"
                    disabled={isSubmitting}
                    addonAfter={tdsValue}
                  />
                </Form.Item>
              </Space>
            )
          );
        }}
      </Form.Item>

      <Form.Item label="Upload Invoice" name="url">
        <Dragger
          name="file"
          multiple={false}
          customRequest={async ({ file, onSuccess, onError }) => {
            try {
              await handleFileUpload(file as File);
              // onSuccess && onSuccess("ok");
            } catch (err) {
              onError && onError(err as Error);
            }
          }}
          showUploadList={false}
          disabled={isSubmitting}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: "#1890ff" }} />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">Upload Invoice here</p>
        </Dragger>
        {fileUrl && (
          <div style={{ marginTop: 8 }}>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer">
              file.png
            </a>
          </div>
        )}
      </Form.Item>

      <Form.Item>
        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button
            onClick={onCancel}
            disabled={isSubmitting}
            style={{ minWidth: 80 }}
          >
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            disabled={isSubmitting}
            loading={isSubmitting}
            style={{ minWidth: 80 }}
          >
            Save
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
}
